import {
  CHECKOUT_KEY,
  CUSTOMER_CART_KEY,
  CUSTOMER_KEY,
  DISCOUNT_KEY,
  ELEVAR_COOKIES_KEY,
  LAST_PASSED_DISCOUNT_APPLIED_KEY,
  PASSED_DISCOUNT_KEY,
  RECENTLY_VIEWED_PRODUCTS_KEY,
  SELECTED_PRODUCTS_KEY,
  UTM_PARAMS_KEY,
  VISITOR_INFO_KEY,
} from "@constants/storageKeys"

type JsonValue = unknown

const MEMORY_FALLBACK: Record<string, string> = {}

const STORAGE_TEST_KEY = "__uag_storage_test__"
const DEBUG =
  typeof process !== "undefined" &&
  process.env &&
  (process.env.GATSBY_STORAGE_DEBUG === "1" ||
    process.env.GATSBY_STORAGE_DEBUG === "true")

const CRITICAL_KEYS = new Set<string>([
  CHECKOUT_KEY,
  CUSTOMER_CART_KEY,
  CUSTOMER_KEY,
  DISCOUNT_KEY,
  LAST_PASSED_DISCOUNT_APPLIED_KEY,
  PASSED_DISCOUNT_KEY,
])

// Keys that are safe to evict when we're out of space
const EVICTABLE_KEYS = new Set<string>([
  ELEVAR_COOKIES_KEY,
  RECENTLY_VIEWED_PRODUCTS_KEY,
  SELECTED_PRODUCTS_KEY,
  UTM_PARAMS_KEY,
  VISITOR_INFO_KEY,
])

type WrappedValue = {
  __expires?: number // Epoch ms
  __uag_wrapped: true
  __value: JsonValue
}

const now = (): number => Date.now()

export function getLocalStorage<T = any>(key: string): null | T {
  const s = getStorage("local")
  if (s) {
    const v = unwrap<T>(s.getItem(key))
    if (v !== null && v !== undefined) {
      return v
    }
  }
  const ss = getStorage("session")
  if (ss) {
    const v = unwrap<T>(ss.getItem(key))
    if (v !== null && v !== undefined) {
      return v
    }
  }
  return unwrap<T>(MEMORY_FALLBACK[key] ?? null)
}

export function getSessionStorage<T = any>(key: string): null | T {
  const ss = getStorage("session")
  if (ss) {
    const v = unwrap<T>(ss.getItem(key))
    if (v !== null && v !== undefined) {
      return v
    }
  }
  return unwrap<T>(MEMORY_FALLBACK[key] ?? null)
}

export function removeLocalStorage(key: string): void {
  const s = getStorage("local")
  if (s) {
    try {
      s.removeItem(key)
    } catch {
      // no-op
    }
  }
  const ss = getStorage("session")
  if (ss) {
    try {
      ss.removeItem(key)
    } catch {
      // no-op
    }
  }
  delete MEMORY_FALLBACK[key]
}

/**
 * Write to localStorage with quota handling and fallbacks.
 * - If quota is exceeded, evict non-critical keys and retry.
 * - If it still fails:
 *   - for non-critical keys, fall back to sessionStorage.
 *   - for critical keys, also mirror to in-memory so the app can proceed.
 *
 * Optionally pass a TTL in ms via third argument for keys that can expire.
 */
export function writeLocalStorage(
  key: string,
  value: JsonValue,
  ttlMs?: number
): boolean {
  // If key is evictable and no TTL provided, default to 30 days
  const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000
  const effectiveTtl =
    (ttlMs === undefined || ttlMs === null) && EVICTABLE_KEYS.has(key)
      ? DEFAULT_TTL_MS
      : ttlMs
  const s = getStorage("local")
  const wrapped = wrap(value, effectiveTtl as number | undefined)
  if (s) {
    const ok = trySetItem(s, key, wrapped)
    if (ok) {
      return true
    }
  }
  if (!CRITICAL_KEYS.has(key)) {
    const ss = getStorage("session")
    if (ss && trySetItem(ss, key, wrapped)) {
      logDebug("wrote to sessionStorage as fallback", key)
      return true
    }
  }
  MEMORY_FALLBACK[key] = wrapped
  logDebug("stored in-memory as last resort", key)
  return false
}

/**
 * Session storage direct writes (rarely needed). These are still wrapped for TTL support.
 */
export function writeSessionStorage(
  key: string,
  value: JsonValue,
  ttlMs?: number
): boolean {
  const ss = getStorage("session")
  const wrapped = wrap(value, ttlMs)
  if (ss && trySetItem(ss, key, wrapped)) {
    return true
  }
  MEMORY_FALLBACK[key] = wrapped
  logDebug("session write fell back to memory", key)
  return false
}

function estimateSizeBytes(str: string): number {
  try {
    return new Blob([str]).size as number
  } catch {
    return str.length
  }
}

function evictToMakeSpace(
  s: Storage,
  bytesNeeded: number,
  protectKey: string
): void {
  pruneExpired(s)
  const candidates: Array<{ key: string; size: number }> = []
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i)
    if (!k || k === protectKey) {
      continue
    }
    if (!EVICTABLE_KEYS.has(k)) {
      continue
    }
    const v = s.getItem(k)
    if (v) {
      candidates.push({ key: k, size: estimateSizeBytes(v) })
    }
  }
  candidates.sort((a, b) => b.size - a.size)
  let freed = 0
  for (const c of candidates) {
    s.removeItem(c.key)
    freed += c.size
    logDebug("evicted", c.key, `~${c.size} bytes`)
    if (freed >= bytesNeeded) {
      break
    }
  }
}

function getStorage(kind: "local" | "session"): null | Storage {
  if (!isBrowser()) {
    return null
  }
  try {
    const s = kind === "local" ? window.localStorage : window.sessionStorage
    s.setItem(STORAGE_TEST_KEY, "1")
    s.removeItem(STORAGE_TEST_KEY)
    return s
  } catch {
    return null
  }
}

function isBrowser(): boolean {
  return typeof window !== "undefined"
}

function logDebug(...args: any[]) {
  if (DEBUG && typeof console !== "undefined") {
    console.info("[storage]", ...args)
  }
}

function pruneExpired(s: Storage): void {
  try {
    const toRemove: string[] = []
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i)
      if (!k) {
        continue
      }
      const raw = s.getItem(k)
      const parsed = safeParse<any>(raw)
      if (
        parsed &&
        typeof parsed === "object" &&
        parsed.__uag_wrapped &&
        typeof parsed.__expires === "number" &&
        parsed.__expires < now()
      ) {
        toRemove.push(k)
      }
    }
    toRemove.forEach((k) => {
      s.removeItem(k)
      logDebug("pruned expired key", k)
    })
  } catch {
    // no-op
  }
}

function safeParse<T = any>(s: null | string): null | T {
  if (!s) {
    return null
  }
  try {
    return JSON.parse(s) as T
  } catch {
    return null
  }
}

function trySetItem(s: Storage, key: string, value: string): boolean {
  try {
    s.setItem(key, value)
    return true
  } catch (e: any) {
    const msg = String((e && (e.name || e.message)) || e)
    const quota =
      e &&
      (e.name === "QuotaExceededError" ||
        e.name === "NS_ERROR_DOM_QUOTA_REACHED")
    if (quota) {
      evictToMakeSpace(s, estimateSizeBytes(value), key)
      try {
        s.setItem(key, value)
        return true
      } catch (e2) {
        logDebug("Quota still exceeded after eviction for key", key, e2)
        return false
      }
    }
    logDebug("setItem failed for key", key, msg)
    return false
  }
}

function unwrap<T = any>(raw: null | string): null | T {
  const parsed = safeParse<T | WrappedValue>(raw)
  if (!parsed) {
    return null
  }
  if (typeof parsed === "object" && parsed && (parsed as any).__uag_wrapped) {
    const w = parsed as WrappedValue
    if (typeof w.__expires === "number" && w.__expires < now()) {
      return null
    }
    return w.__value as unknown as T
  }
  return parsed as T
}

function wrap(value: JsonValue, ttlMs?: number): string {
  if (ttlMs && ttlMs > 0) {
    const w: WrappedValue = {
      __expires: now() + ttlMs,
      __uag_wrapped: true,
      __value: value,
    }
    return JSON.stringify(w)
  }
  return JSON.stringify(value)
}

// Best-effort maintenance on load: prune expired values early
;(function bootstrapPrune() {
  const s = getStorage("local")
  if (s) {
    pruneExpired(s)
  }
})()
